<script setup lang="ts">
    import { computed, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useChatbotAuth } from '~/composables/useChatbotAuth'
    import { useChatbotProfileProvider } from '~/composables/useChatbotProfileProvider'
    import { useChatbotAgent } from '~/composables/useChatbotAgent'
    import { useChatbotAuthForm } from '~/composables/useChatbotAuthForm'
    import { useChatbotStoreSync } from '~/composables/useChatbotStoreSync'
    import { useFocusTrapBypass } from '~/composables/useFocusTrapBypass'
    import { BACKEND_URL } from '~/utils'
    import {
        initializeAppInsights,
        setTelemetryContext,
        trackAppException,
    } from '~/modules/applicationInsights'
    import PkChatbot from 'components/src/chat/PkChatbot.vue'
    import PkChatbotAuth from 'components/src/chat/PkChatbotAuth.vue'
    import PkLoader from 'components/src/PkLoader.vue'
    import { useDialog } from 'composables'
    import { useWidgetEvents } from '~/utils/emitWidgetEvent'

    defineOptions({
        inheritAttrs: false,
    })

    useFocusTrapBypass()

    const props = withDefaults(
        defineProps<{
            agentId: string
            organizationSlug: string
            baseUrl?: string
            externalToken?: string
            /** Boolean or attribute string ("true"/"false") when set via HTML attribute. */
            awaitExternalToken?: boolean | string
            /**
             * Host-session mode: the widget skips its own sign-in and uses
             * the host platform's better-auth session from the cookie.
             * Boolean or attribute string ("true"/"false").
             */
            useHostSession?: boolean | string
            /** Comma-separated list of extra slot names to forward to PkChatbot (e.g. "tool-showArtifact,subagent"). */
            forwardedSlots?: string
            customCss?: string
            appinsightsConnectionString?: string
            /** Override the session/anonymous cookie name (defaults to 'eight-wave-ai'). */
            cookieName?: string
            /** Layout mode: `widget` (default, contained with header), `fullscreen` (full-page app layout) or `panel` (compact chrome for a docked side panel; the host owns background, border and open/close). */
            displayMode?: 'widget' | 'fullscreen' | 'panel'
            /**
             * Force the chat color scheme instead of following the OS
             * `prefers-color-scheme`. Let a host app hand down its own theme.
             */
            systemTheme?: 'light' | 'dark'
        }>(),
        {
            baseUrl: BACKEND_URL,
            externalToken: undefined,
            awaitExternalToken: false,
            useHostSession: false,
            forwardedSlots: undefined,
            customCss: undefined,
            appinsightsConnectionString: undefined,
            cookieName: undefined,
            displayMode: 'widget',
            systemTheme: undefined,
        },
    )

    // dialog
    const { PkGlobalDialog } = useDialog()

    // Widget events (must be called during setup to capture the component instance)
    const { emit: emitWidgetEvent } = useWidgetEvents()

    // chatbot ref
    const chatbotEl = ref<InstanceType<typeof PkChatbot>>()

    // external context set by host application
    const externalContext = ref<Record<string, unknown> | undefined>()

    // Initialize Application Insights (singleton — first call wins)
    initializeAppInsights(props.appinsightsConnectionString)
    setTelemetryContext({
        organizationSlug: props.organizationSlug,
        agentId: props.agentId,
    })

    const {
        session,
        onError: onAuthError,
        headers,
        token,
        userId,
        isExternalAuth,
        signInWithEmail,
        twoFactorChallenge,
        verifyTwoFactor,
        sendTwoFactorCode,
        cancelTwoFactor,
        signUpWithEmail,
        requestPasswordReset,
        signInWithSocialPopup,
        signInWithExternalToken,
        signOut,
    } = useChatbotAuth({
        baseUrl: props.baseUrl,
        externalToken: () => props.externalToken,
        awaitExternalToken:
            !!props.awaitExternalToken && props.awaitExternalToken !== 'false',
        useHostSession: () =>
            !!props.useHostSession && props.useHostSession !== 'false',
        cookieName: props.cookieName,
        onSignedIn: (detail) => emitWidgetEvent('signed-in', detail),
        onSignedOut: () => emitWidgetEvent('signed-out'),
        onExternalAuthExpired: () => emitWidgetEvent('external-auth-expired'),
    })

    // Enrich telemetry with userId once auth completes
    watch(userId, (id) => {
        if (id) {
            setTelemetryContext({ userId: id })
        }
    })

    // The chat store outlives PkChatbot (v-if'd on the session): keep the
    // session identity mirrored into it so the identity-switch reset starts
    // as soon as the new identity is known.
    useChatbotStoreSync({
        agentId: props.agentId,
        baseUrl: props.baseUrl,
        headers,
        userId,
    })

    // emit 'ready' once the session is established
    watch(
        () => session.value.data,
        (data) => {
            if (data) {
                emitWidgetEvent('ready')
            }
        },
    )

    function onError(error: { type: string; title: string; status: number }) {
        trackAppException(new Error(`${error.type}: ${error.title}`), {
            status: String(error.status),
        })
        emitWidgetEvent('error', {
            type: error.type,
            title: error.title,
            status: error.status,
        })
        onAuthError(error)
    }

    // i18n
    const { t } = useI18n({ useScope: 'global' })

    const {
        agent,
        branding,
        authMethods,
        requiresAuth,
        notFound,
        isLoading,
        reload,
        themeClass,
        themeStyle,
    } = useChatbotAgent({
        baseUrl: props.baseUrl,
        organizationSlug: () => props.organizationSlug,
        agentId: () => props.agentId,
        headers,
        systemTheme: () => props.systemTheme,
    })

    // Read-only profile context + voluntary sign-in affordance shared by both
    // widgets. Provides ChatbotProfileContextKey for the chat components.
    const { authBranding } = useChatbotProfileProvider({
        agent,
        branding,
        authMethods,
        session,
        signOut,
        isExternalAuth,
        token,
        baseUrl: props.baseUrl,
        headers,
        cookieName: props.cookieName,
    })

    // Login screen (private agents and the voluntary `view-auth` slot)
    const {
        authError,
        authSuccess,
        authLoading,
        handleSignIn,
        handleSignUp,
        handleReset,
        handleSocial,
        handleVerifyTwoFactor,
        handleSendTwoFactorCode,
        handleCancelTwoFactor,
    } = useChatbotAuthForm({
        signInWithEmail,
        isTwoFactorPending: () => !!twoFactorChallenge.value,
        verifyTwoFactor,
        sendTwoFactorCode,
        cancelTwoFactor,
        signUpWithEmail,
        requestPasswordReset,
        signInWithSocialPopup,
        refetchSession: () => session.value.refetch(),
        reload,
    })

    const extraSlotNames = computed(() =>
        props.forwardedSlots
            ? props.forwardedSlots
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
            : [],
    )

    // Greeting personalization: only real (non-anonymous) names
    const userName = computed(() =>
        session.value.data?.user?.isAnonymous
            ? undefined
            : session.value.data?.user?.name,
    )

    const mergedCss = computed(() => {
        const parts = [
            agent.value?.interface?.customCss,
            branding.value?.interface.customCss,
            props.customCss,
        ].filter(Boolean)
        return parts.length ? parts.join('\n') : undefined
    })

    // Public API exposed on the custom element DOM node
    defineExpose({
        sendMessage(text: string) {
            chatbotEl.value?.sendMessage(text)
        },
        startNewChat() {
            chatbotEl.value?.startNewChat()
        },
        setContext(context: Record<string, unknown>) {
            externalContext.value = context
        },
        signIn: signInWithExternalToken,
        signOut,
    })
</script>

<template>
    <div
        class="embedded-chat-widget-root"
        :class="themeClass"
        :style="themeStyle">
        <!-- #region styles -->
        <component :is="'style'" v-if="mergedCss">{{ mergedCss }}</component>
        <!-- #endregion -->

        <!-- #region not found -->
        <div
            v-if="notFound"
            class="embedded-chat-widget embedded-chat-widget--not-found">
            <p>{{ t('message.agentNotFound') }}</p>
        </div>
        <!-- #endregion -->

        <!-- #region auth -->
        <Transition name="fade" appear>
            <div
                v-if="
                    (agent || branding) &&
                    !notFound &&
                    requiresAuth &&
                    (!session.data?.user?.id || session.data?.user?.isAnonymous)
                "
                class="embedded-chat-widget embedded-chat-widget--auth">
                <PkChatbotAuth
                    v-if="authMethods"
                    v-model:error-message="authError"
                    v-model:success="authSuccess"
                    :branding="branding"
                    :auth-methods="authMethods"
                    :loading="authLoading"
                    :two-factor-methods="twoFactorChallenge?.methods"
                    @sign-in="handleSignIn"
                    @sign-up="handleSignUp"
                    @request-reset="handleReset"
                    @social="handleSocial"
                    @verify-two-factor="handleVerifyTwoFactor"
                    @send-two-factor-code="handleSendTwoFactorCode"
                    @cancel-two-factor="handleCancelTwoFactor" />
                <PkLoader v-else />
            </div>
        </Transition>
        <!-- #endregion -->

        <!-- #region loading -->
        <Transition name="fade" appear>
            <div
                v-if="isLoading && !notFound"
                class="embedded-chat-widget embedded-chat-widget--loading">
                <PkLoader />
            </div>
        </Transition>
        <!-- #endregion -->

        <!-- #region chatbot -->
        <PkChatbot
            v-if="agent && session.data && !notFound"
            ref="chatbotEl"
            class="embedded-chat-widget"
            :base-url
            :agent-id
            :user-id
            :name="agent.name"
            :agent-interface="agent.interface"
            :agent-file-upload="agent.capabilities?.fileUpload"
            :voice="agent.capabilities?.voice"
            :modifier="displayMode"
            :user-name="userName"
            :headers
            :system-theme="systemTheme"
            :actions="['upvote', 'downvote', 'feedback', 'regenerate']"
            :external-context="externalContext"
            @error="onError">
            <template #header-actions>
                <slot name="header-actions" />
            </template>
            <template #actions>
                <slot name="actions" />
            </template>
            <!-- Voluntary sign-in view for anonymous users (chat dropdown) -->
            <template #view-auth>
                <PkChatbotAuth
                    v-model:error-message="authError"
                    v-model:success="authSuccess"
                    :branding="authBranding"
                    :auth-methods="authMethods"
                    :loading="authLoading"
                    :two-factor-methods="twoFactorChallenge?.methods"
                    @sign-in="handleSignIn"
                    @sign-up="handleSignUp"
                    @request-reset="handleReset"
                    @social="handleSocial"
                    @verify-two-factor="handleVerifyTwoFactor"
                    @send-two-factor-code="handleSendTwoFactorCode"
                    @cancel-two-factor="handleCancelTwoFactor" />
            </template>
            <template v-for="name in extraSlotNames" #[name]>
                <slot :name="name" />
            </template>
        </PkChatbot>
        <!-- #endregion -->

        <!-- #region global dialogs -->
        <PkGlobalDialog />
        <!-- #endregion -->
    </div>
</template>

<style lang="scss">
    @use '~/assets/scss/style';

    .embedded-chat-widget-root {
        display: contents;
    }

    .embedded-chat-widget {
        width: 100%;
        height: 100%;

        &--loading,
        &--not-found {
            display: flex;
            align-items: center;
            justify-content: center;
        }

        &--auth {
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.2s ease;
    }

    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
    }
</style>
